import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, ProgressBar, Button, Tab, Tabs, Table, Alert, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { auth, db } from '../firebase';
import { doc, updateDoc, collection, getDocs, query, where } from 'firebase/firestore';
import FacilitatorNavbar from '../components/FacilitatorNavbar';
import FacilitatorProgramView from '../components/FacilitatorProgramView';
import PreviousSessionResources from '../components/PreviousSessionResources';
import ReadAIAccountConfig from '../components/ReadAIAccountConfig';
import programAssignmentService from '../services/programAssignmentService';
import { SOUTH_AFRICAN_PROVINCES } from '../constants/addressConstants';
import '../styles/brand.css';

const FacilitatorProfile = () => {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [personalInfo, setPersonalInfo] = useState({
    displayName: '',
    email: '',
    phone: '',
    department: '',
    specialization: '',
    bio: '',
    streetAddress: '',
    city: '',
    province: '',
    postalCode: ''
  });
  const [cohorts, setCohorts] = useState([]);
  const [modules, setModules] = useState([]);
  const [assignedPrograms, setAssignedPrograms] = useState([]);
  const [stats, setStats] = useState({
    totalLearners: 0,
    activeCohorts: 0,
    completedModules: 0,
    averageAttendance: 0,
    assignedPrograms: 0
  });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    if (currentUser && userProfile) {
      loadProfileData();
    }
  }, [currentUser, userProfile]);

  const loadProfileData = async () => {
    try {
      setLoading(true);

      // Set personal information from userProfile
      setPersonalInfo({
        displayName: userProfile.displayName || '',
        email: userProfile.email || currentUser.email || '',
        phone: userProfile.phone || '',
        department: userProfile.department || '',
        specialization: userProfile.specialization || '',
        bio: userProfile.bio || '',
        streetAddress: userProfile.streetAddress || '',
        city: userProfile.city || '',
        province: userProfile.province || '',
        postalCode: userProfile.postalCode || ''
      });

      // Load facilitator's cohorts
      const cohortsQuery = query(
        collection(db, 'cohorts'),
        where('facilitatorId', '==', currentUser.uid)
      );
      const cohortsSnapshot = await getDocs(cohortsQuery);
      const cohortsData = cohortsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setCohorts(cohortsData);

      // Load facilitator's modules
      const modulesQuery = query(
        collection(db, 'modules'),
        where('facilitatorId', '==', currentUser.uid)
      );
      const modulesSnapshot = await getDocs(modulesQuery);
      const modulesData = modulesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setModules(modulesData);

      // Load assigned programs
      let programsData = [];
      try {
        devLog('Loading programs for facilitator:', currentUser.uid);
        programsData = await programAssignmentService.getFacilitatorPrograms();
        devLog('Loaded programs:', programsData);
        setAssignedPrograms(programsData);
      } catch (error) {
        devLog('Could not load assigned programs:', error);
        setAssignedPrograms([]);
      }

      // Calculate stats
      const totalLearners = cohortsData.reduce((sum, cohort) => sum + (cohort.enrolledCount || 0), 0);
      const activeCohorts = cohortsData.filter(cohort => cohort.status === 'active').length;
      const completedModules = modulesData.filter(module => module.status === 'completed').length;
      const averageAttendance = 0; // No attendance data yet - would be calculated from actual attendance

      setStats({
        totalLearners,
        activeCohorts,
        completedModules,
        averageAttendance,
        assignedPrograms: programsData.length
      });

    } catch (error) {
      devError('Error loading profile data:', error);
      setAlert({
        show: true,
        message: 'Failed to load profile data. Please try again.',
        type: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        displayName: personalInfo.displayName,
        phone: personalInfo.phone,
        department: personalInfo.department,
        specialization: personalInfo.specialization,
        bio: personalInfo.bio,
        streetAddress: personalInfo.streetAddress,
        city: personalInfo.city,
        province: personalInfo.province,
        postalCode: personalInfo.postalCode,
        addressLastUpdated: new Date(),
        updatedAt: new Date()
      });

      setAlert({
        show: true,
        message: 'Profile updated successfully!',
        type: 'success'
      });
      setEditing(false);
    } catch (error) {
      devError('Error updating profile:', error);
      setAlert({
        show: true,
        message: 'Failed to update profile. Please try again.',
        type: 'danger'
      });
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Not set';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const getCohortStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge bg="success">Active</Badge>;
      case 'completed':
        return <Badge bg="secondary">Completed</Badge>;
      case 'upcoming':
        return <Badge bg="warning">Upcoming</Badge>;
      default:
        return <Badge bg="light" text="dark">Unknown</Badge>;
    }
  };

  const getModuleStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge bg="primary">Active</Badge>;
      case 'completed':
        return <Badge bg="success">Completed</Badge>;
      case 'draft':
        return <Badge bg="secondary">Draft</Badge>;
      default:
        return <Badge bg="light" text="dark">Unknown</Badge>;
    }
  };

  if (userProfile?.role !== 'facilitator') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>Only facilitators can access this profile page.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="mt-5">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading your profile...</p>
        </div>
      </Container>
    );
  }

  return (
    <>
      <FacilitatorNavbar />

      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">My Profile</h1>
              <p className="mission-text mb-4">
                Manage your facilitator profile and track your impact
              </p>
              <p className="mb-0">
                View your teaching statistics, manage personal information, and monitor your cohorts and modules.
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              <Button
                variant="outline-light"
                onClick={loadProfileData}
                disabled={loading}
              >
                {loading ? 'Refreshing...' : 'Refresh Data'}
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">
        {alert.show && (
          <Alert variant={alert.type} dismissible onClose={() => setAlert({ show: false })}>
            {alert.message}
          </Alert>
        )}

        {/* Statistics Overview */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center stats-card">
              <Card.Body>
                <div className="stats-number">{stats.totalLearners}</div>
                <div className="stats-label">Total Learners</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center stats-card">
              <Card.Body>
                <div className="stats-number">{stats.activeCohorts}</div>
                <div className="stats-label">Active Cohorts</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center stats-card">
              <Card.Body>
                <div className="stats-number">{stats.completedModules}</div>
                <div className="stats-label">Completed Modules</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center stats-card">
              <Card.Body>
                <div className="stats-number">{stats.assignedPrograms}</div>
                <div className="stats-label">Assigned Programs</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Profile Details */}
        <Tabs defaultActiveKey="personal" className="mb-4">
          <Tab eventKey="personal" title="Personal Information">
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Personal Information</h5>
                <div>
                  {editing ? (
                    <>
                      <Button variant="success" size="sm" onClick={handleSaveProfile} className="me-2">
                        Save Changes
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setEditing(false)}>
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline-primary" size="sm" onClick={() => setEditing(true)}>
                      Edit Profile
                    </Button>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Full Name</Form.Label>
                      {editing ? (
                        <Form.Control
                          type="text"
                          value={personalInfo.displayName}
                          onChange={(e) => setPersonalInfo({...personalInfo, displayName: e.target.value})}
                        />
                      ) : (
                        <p className="form-control-plaintext">{personalInfo.displayName || 'Not set'}</p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <p className="form-control-plaintext">{personalInfo.email}</p>
                      <small className="text-muted">Email cannot be changed</small>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Phone Number</Form.Label>
                      {editing ? (
                        <Form.Control
                          type="text"
                          value={personalInfo.phone}
                          onChange={(e) => setPersonalInfo({...personalInfo, phone: e.target.value})}
                          placeholder="Enter phone number"
                        />
                      ) : (
                        <p className="form-control-plaintext">{personalInfo.phone || 'Not set'}</p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Department</Form.Label>
                      {editing ? (
                        <Form.Control
                          type="text"
                          value={personalInfo.department}
                          onChange={(e) => setPersonalInfo({...personalInfo, department: e.target.value})}
                          placeholder="Enter department"
                        />
                      ) : (
                        <p className="form-control-plaintext">{personalInfo.department || 'Not set'}</p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Specialization</Form.Label>
                      {editing ? (
                        <Form.Control
                          type="text"
                          value={personalInfo.specialization}
                          onChange={(e) => setPersonalInfo({...personalInfo, specialization: e.target.value})}
                          placeholder="Enter your area of specialization"
                        />
                      ) : (
                        <p className="form-control-plaintext">{personalInfo.specialization || 'Not set'}</p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Bio</Form.Label>
                      {editing ? (
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={personalInfo.bio}
                          onChange={(e) => setPersonalInfo({...personalInfo, bio: e.target.value})}
                          placeholder="Tell us about yourself..."
                        />
                      ) : (
                        <p className="form-control-plaintext">{personalInfo.bio || 'No bio added yet'}</p>
                      )}
                    </Form.Group>
                  </Col>

                  <Col md={12}>
                    <hr className="my-4" />
                    <h6 className="mb-3">Address Information</h6>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Street Address</Form.Label>
                      {editing ? (
                        <Form.Control
                          type="text"
                          value={personalInfo.streetAddress}
                          onChange={(e) => setPersonalInfo({...personalInfo, streetAddress: e.target.value})}
                          placeholder="Enter street address"
                        />
                      ) : (
                        <p className="form-control-plaintext">{personalInfo.streetAddress || 'Not set'}</p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>City</Form.Label>
                      {editing ? (
                        <Form.Control
                          type="text"
                          value={personalInfo.city}
                          onChange={(e) => setPersonalInfo({...personalInfo, city: e.target.value})}
                          placeholder="Enter city"
                        />
                      ) : (
                        <p className="form-control-plaintext">{personalInfo.city || 'Not set'}</p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Province</Form.Label>
                      {editing ? (
                        <Form.Select
                          value={personalInfo.province}
                          onChange={(e) => setPersonalInfo({...personalInfo, province: e.target.value})}
                        >
                          <option value="">Select province...</option>
                          {SOUTH_AFRICAN_PROVINCES.map(prov => (
                            <option key={prov} value={prov}>{prov}</option>
                          ))}
                        </Form.Select>
                      ) : (
                        <p className="form-control-plaintext">{personalInfo.province || 'Not set'}</p>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Postal Code</Form.Label>
                      {editing ? (
                        <Form.Control
                          type="text"
                          value={personalInfo.postalCode}
                          onChange={(e) => setPersonalInfo({...personalInfo, postalCode: e.target.value})}
                          placeholder="4 digits"
                          maxLength={4}
                          pattern="\d{4}"
                        />
                      ) : (
                        <p className="form-control-plaintext">{personalInfo.postalCode || 'Not set'}</p>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="cohorts" title={`My Cohorts (${cohorts.length})`}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Assigned Cohorts</h5>
              </Card.Header>
              <Card.Body>
                {cohorts.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">No cohorts assigned yet.</p>
                  </div>
                ) : (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Cohort Name</th>
                        <th>Program</th>
                        <th>Status</th>
                        <th>Enrolled</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cohorts.map((cohort) => (
                        <tr key={cohort.id}>
                          <td>
                            <strong>{cohort.name}</strong>
                          </td>
                          <td>{cohort.program || 'N/A'}</td>
                          <td>{getCohortStatusBadge(cohort.status)}</td>
                          <td>{cohort.enrolledCount || 0}</td>
                          <td>{formatDate(cohort.startDate)}</td>
                          <td>{formatDate(cohort.endDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="programs" title="My Programs">
            <FacilitatorProgramView />
          </Tab>

          <Tab eventKey="modules" title={`My Modules (${modules.length})`}>
            <Card>
              <Card.Header>
                <h5 className="mb-0">Created Modules</h5>
              </Card.Header>
              <Card.Body>
                {modules.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted">No modules created yet.</p>
                    <Button variant="primary" onClick={() => navigate('/facilitator/module-builder')}>
                      Create Your First Module
                    </Button>
                  </div>
                ) : (
                  <Table striped bordered hover responsive>
                    <thead>
                      <tr>
                        <th>Module Name</th>
                        <th>Status</th>
                        <th>Cohorts</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {modules.map((module) => (
                        <tr key={module.id}>
                          <td>
                            <strong>{module.title}</strong>
                            <br />
                            <small className="text-muted">{module.description}</small>
                          </td>
                          <td>{getModuleStatusBadge(module.status)}</td>
                          <td>{module.cohortIds ? module.cohortIds.length : 0}</td>
                          <td>{formatDate(module.createdAt)}</td>
                          <td>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => navigate(`/module/${module.id}`)}
                            >
                              View Module
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="session-resources" title="📹 Previous Session Resources">
            <Card>
              <Card.Header>
                <h5 className="mb-0">📹 Previous Session Resources</h5>
                <p className="text-muted mb-0 small">
                  Manage meeting recordings and notes from ReadAI
                </p>
              </Card.Header>
              <Card.Body>
                <PreviousSessionResources />
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="readai-config" title="⚙️ ReadAI Configuration">
            <Card>
              <Card.Header>
                <h5 className="mb-0">⚙️ ReadAI Integration Configuration</h5>
                <p className="text-muted mb-0 small">
                  Configure your ReadAI account and integration settings
                </p>
              </Card.Header>
              <Card.Body>
                <ReadAIAccountConfig />
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>
    </>
  );
};

export default FacilitatorProfile;